import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import {
  IconButton,
  Drawer,
  Box,
  Typography,
  TextField,
  Stack,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputAdornment,
  Badge,
  Alert,
  LinearProgress,
  useMediaQuery,
  Tooltip,
  Button,
} from "@mui/material";
import MessageIcon from "@mui/icons-material/Message";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import axios from "axios";
import { useAuth } from "react-oidc-context";
import {
  backendUrl,
  customerName,
  apiVersion,
} from "../../constants/constants";

const CommentFeature = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [commentBtn, setCommentBtn] = useState(true);
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [loadReverse, setLoadReverse] = useState(false);
  const [reverse, setReverse] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (showSuccessAlert) {
      const timer = setTimeout(() => setShowSuccessAlert(false), 3000);
      return () => clearTimeout(timer);
    }
    if (showErrorAlert) {
      const timer = setTimeout(() => setShowErrorAlert(false), 3000);
      return () => clearTimeout(timer);
    }
    getApplicationComments();
  }, [showSuccessAlert, showErrorAlert, reverse]);

  const auth = useAuth();
  const token = auth.user?.id_token;
  const { applicationId } = useParams();

  const handleToggle = () => {
    setOpen(!open);
    setCommentBtn(!commentBtn);
  };

  const handleAddComment = async () => {
    if (comment.trim() === "") return;
    setShowProgress(true);
    try {
      const apiUrl = `${backendUrl}/${apiVersion}/createJobApplicationComment`;
      const response = await axios.post(
        apiUrl,
        {
          applicationId: applicationId,
          content: comment,
        },
        {
          headers: {
            "Content-Type": "application/json",
            applicationId: applicationId,
            customerName: customerName || window.location.host.split(".")[0],
            Authorization: `Bearer ${token}`,
          },
        }
      );
      response.status === 200 && setShowSuccessAlert(true);
      setShowProgress(false);
      setComment("");
      getApplicationComments();
    } catch (error) {
      setShowErrorAlert(true);
      setShowProgress(false);
      console.log("error:", error);
    }
  };

  const getApplicationComments = async () => {
    setLoadReverse(true);
    try {
      const apiUrl = `${backendUrl}/${apiVersion}/jobApplicationComments`;
      const response = await axios.get(apiUrl, {
        headers: {
          "Content-Type": "application/json",
          applicationId: applicationId,
          customerName: customerName || window.location.host.split(".")[0],
          Authorization: `Bearer ${token}`,
        },
      });

      reverse
        ? setComments(
            response?.data?.d?.jobApplicationComments?.results.reverse()
          )
        : setComments(response?.data?.d?.jobApplicationComments?.results);
      setLoadReverse(false);
    } catch (error) {
      console.log("error:", error);
    }
  };

  return (
    <Box>
      {commentBtn && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
          }}
        >
          <IconButton
            color="primary"
            sx={{
              bgcolor: "white",
              boxShadow: 3,
              "&:hover": { bgcolor: "grey.100" },
            }}
            onClick={handleToggle}
          >
            <MessageIcon />
          </IconButton>
        </Box>
      )}
      <Drawer
        anchor="right"
        open={open}
        onClose={handleToggle}
        PaperProps={{
          sx: isMobile
            ? {
                width: "100%",
                height: "90%",
                position: "fixed",
                top: "auto",
                bottom: 0,
              }
            : {
                width: 325,
                height: 580,
                position: "fixed",
                top: "auto",
                bottom: 16,
                right: 16,
                borderRadius: "12px",
              },
        }}
      >
        <Box display="flex" flexDirection="column" height="100%">
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1,
              borderBottom: "1px solid #ddd",
              color: "black",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              <Stack direction="row" spacing={3} alignItems="center">
                <Typography variant="h6">{t("comment.title")}</Typography>
                {comments.length > 0 && (
                  <Badge
                    badgeContent={comments.length}
                    color="primary"
                    sx={{ "& .MuiBadge-badge": { ml: 1 } }}
                  />
                )}

                {/* Sort button with both arrows */}
                {comments.length > 1 && (
                  <Tooltip title={t("comment.tooltipTxt")} placement="bottom">
                    <Button
                      size="small"
                      sx={{
                        disabled: "flex",
                      }}
                      onClick={() => setReverse(!reverse)}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                      <ArrowDownwardIcon fontSize="small" />
                    </Button>
                  </Tooltip>
                )}
              </Stack>

              <IconButton onClick={handleToggle}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Comment List */}
          {loadReverse && (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}
          <Box sx={{ flexGrow: 1, overflowY: "auto", px: 2 }}>
            <List>
              {comments.map((comment) => (
                <React.Fragment key={comment.commentId}>
                  <ListItem
                    alignItems="flex-start"
                    sx={{
                      paddingLeft: 0,
                      paddingRight: 0,
                      paddingTop: 0.5,
                      paddingBottom: 0.5,
                    }}
                  >
                    <ListItemText
                      primary={comment.commentatorNav.displayName}
                      secondary={comment.content}
                    />
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))}
            </List>
          </Box>
          {showSuccessAlert && (
            <Alert
              severity="success"
              onClose={() => setShowSuccessAlert(false)}
            >
              {t("comment.success")}
            </Alert>
          )}
          {showErrorAlert && (
            <Alert severity="error" onClose={() => setShowErrorAlert(false)}>
              {t("comment.error")}
            </Alert>
          )}
          {showProgress && (
            <Box sx={{ width: "100%" }}>
              <LinearProgress />
            </Box>
          )}

          <Box>
            <TextField
              fullWidth
              size="large"
              multiline
              minRows={1}
              maxRows={6}
              placeholder={t("comment.typeComment")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddComment()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      color="primary"
                      onClick={handleAddComment}
                      disabled={showProgress}
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default CommentFeature;
